import React from 'react';
import { assetIcon, fileIcon, extension } from '../../../../../general/assets';
import { StringHelpers } from 'e4shared';
import config from '../../../../../../config';

export const OneDrive = ({ assets, allowMultiple, onAdd, mediaOnly, token }) => {
    const baseUrl = 'https://graph.microsoft.com/v1.0/';

    const [files, setFiles] = React.useState(assets || []);
    const [drive, setDrive] = React.useState('');
    const [itemId, setItemId] = React.useState('');
    const [children, setChildren] = React.useState([]);
    const [crumb, setCrumb] = React.useState([{ id: '', name: 'One Drive' }]);
    const [error, setError] = React.useState('');
    const [accessToken, setAccessToken] = React.useState(token || sessionStorage.getItem('access_token'));

    React.useEffect(() => {
        if (files) {
            onAdd(files, 'onedrive');
        }
    }, [files]);

    React.useEffect(() => {
        const messageHandler = message => {
            const { accessToken, expiresOn } = message.data;
            accessToken && sessionStorage.setItem('access_token', accessToken);
            accessToken && sessionStorage.setItem('token_expiration', expiresOn);
            accessToken && setAccessToken(accessToken);
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, []); // empty array => run only once

    React.useEffect(() => {
        if (accessToken) {
            const rootUrl = 'me/drive/root/children';
            const driveUrl = `drives/${drive}/items/${itemId}/children`;
            const url = `${baseUrl}${itemId ? driveUrl : rootUrl}`;

            fetch(url, {
                method: 'get',
                headers: new Headers({
                    Authorization: 'Bearer ' + accessToken,
                }),
            })
                .then(response => response.json())
                .then(response => {
                    const items = response.value
                        ? response.value.map(x => {
                              return {
                                  id: x.id,
                                  internal_Id: x.id,
                                  name: x.name,
                                  drive: x.parentReference.driveId,
                                  parent: x.parentReference.id,
                                  size: x.size,
                                  url: x['@microsoft.graph.downloadUrl'],
                              };
                          })
                        : setAccessToken('');

                    setChildren(items);
                });
        }
    }, [drive, itemId, accessToken]);

    const setShareUrl = file => {
        return new Promise(resolve => {
            const fileUrl = `me/drive/items/${file.internal_Id}/createLink`;
            const url = `${baseUrl}${fileUrl}`;

            const body = JSON.stringify({
                type: 'view',
                organization: 'organisation',
                expirationDateTime: '2099-01-01T00:00:00Z',
            });

            fetch(url, {
                method: 'post',
                body,
                headers: new Headers({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken }),
            })
                .then(response => response.json())
                .then(response => {
                    file.url = response.link ? response.link.webUrl : '';
                    resolve();
                });
        });
    };

    const addFile = async file => {
        setError('');
        if (!files.some(f => f.internal_Id === file.internal_Id)) {
            if (mediaOnly && !StringHelpers.containsExt(file.name, config.mediaExtensions)) {
                setError(
                    `Sorry, but as you have selected to launch this in the video player, you must upload a file with an extension of ${StringHelpers.arrayToString(
                        config.mediaExtensions,
                        ',',
                        'or'
                    )}`
                );
            } else {
                // Get share link.....
                await setShareUrl(file);

                if (allowMultiple) {
                    setFiles(files.concat([file]));
                } else {
                    setFiles([file]);
                }
            }
        }
    };

    const removeFile = remove => {
        setError('');
        setFiles(files.filter(u => u.internal_Id !== remove.internal_Id));
    };

    return (
        <div>
            {accessToken ? (
                <div>
                    <div className="mb5 btn-row">
                        {crumb.map((c, i) => {
                            return (
                                <span key={`onedrive-item-${i}`}>
                                    <button
                                        data-testid="onedrive-item-button"
                                        id="onedrive-item-button"
                                        onClick={() => {
                                            setItemId(c.id);
                                            setCrumb(crumb.slice(0, i + 1));
                                        }}
                                        disabled={i === crumb.length - 1}
                                        className="btn btn-anycase btn-xs btn-primary mr"
                                    >
                                        {c.name}
                                    </button>
                                    {i < crumb.length - 1 && <span className="mr5">...</span>}
                                </span>
                            );
                        })}
                    </div>
                    <div className="graph-files">
                        {children?.map(item => {
                                return (
                                    <div
                                        key={`filesAndFolder-${item.id}`}
                                        className="flex pointer graph-item p5"
                                        title={`Select ${item.name}`}
                                        onClick={() => {
                                            if (item.url) {
                                                addFile(item);
                                            } else {
                                                setCrumb(crumb.concat([item]));
                                                setDrive(item.drive);
                                                setItemId(item.id);
                                            }
                                        }}
                                    >
                                        <div>
                                            <i className={`mr10 fa fa-${item.url ? fileIcon(extension(item)) : 'folder'}`} />
                                        </div>
                                        <div className="flex-grow">{item.name}</div>
                                        <div>{item.size && item.url && StringHelpers.bytesToSize(item.size)}</div>
                                    </div>
                                );
                            })}
                    </div>
                    {error && <div className="mt10 text-danger">{error}</div>}
                    {files.length > 0 && (
                        <div className="mt10">
                            <label>Files</label>
                            {files
                                .sort((a, b) => (a.name > b.name ? 1 : -1))
                                .map((f, i) => {
                                    return (
                                        <div key={`file-${i}`} className="mb5 flex flex-center">
                                            <i className={`fa fa-${assetIcon(f)} mr5`} />
                                            <span className="ellipsis500 flex-grow">
                                                {f.name} - {StringHelpers.bytesToSize(f.size)}
                                            </span>
                                            <i
                                                className="ml5 text-danger fa fa-trash pointer text-right"
                                                title={`Remove the file ${f.name}`}
                                                onClick={() => removeFile(f)}
                                            />
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    <div className="text-center mt100">
                        <button
                            id="login-onedrive-button"
                            className="btn btn-primary"
                            onClick={() => window.open('/onedrive', 'onedrive', 'width=500,height=600')}
                        >
                            <i className="mr5 fa fa-windows" />
                            <span>Login To OneDrive</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OneDrive;
